// Function index:
//   showAdminScreen(user) — mount the sidebar admin dashboard. Resolves when
//       the user clicks "Back to menu". Refuses to render for non-admins
//       (the menu also gates the entry button — defence in depth).
//
// Sidebar nav switches between sections; each panel lives in its own sibling
// under screens/admin/:
//   - overview    — summary stat cards (rendered here from getAdminSystem)
//   - users.ts    — id / email / role / verified, with role-toggle + verify
//   - parties.ts  — id / world / member count + per-party prefs editor
//   - system.ts   — job state counts, table counts, Anthropic spend, failures
//   - secrets.ts  — listed secrets, add/rotate/delete, audit log
//   - usage.ts    — per-user spend breakdown by model and period
//   - audit.ts    — admin_audit action history with filter/search/pagination
//   - world-rules — GM-authored house rules per world, with revoke
//   - mint.ts     — themed-world mint form + live progress polling
//   - llm-model   — override the global LLM model selection, or revert to tier-based
//   - jobs        — the inline job-enqueue form

import {
  ADMIN_JOB_KINDS, enqueueAdminJob, getAdminSystem,
  type AdminJobKind, type AdminSystem, type AuthUser,
} from '../api.js';
import { renderUsers } from './admin/users.js';
import { renderParties } from './admin/parties.js';
import { renderSystem } from './admin/system.js';
import { renderSecrets } from './admin/secrets.js';
import { renderAdminUsage } from './admin/usage.js';
import { renderAudit } from './admin/audit.js';
import { renderWorldRules } from './admin/world-rules.js';
import { renderMintWorld } from './admin/mint.js';
import { renderLlmModel } from './admin/llm-model.js';
import type { AdminPanelCtx } from './admin/helpers.js';

export async function showAdminScreen(user: AuthUser): Promise<void> {
  if (user.role !== 'admin') return;

  const $ = (id: string): HTMLElement => document.getElementById(id)!;
  const screen = $('screen-admin');
  const greeting = $('admin-greeting');
  const overviewEl = $('admin-overview');
  const usersEl = $('admin-users');
  const partiesEl = $('admin-parties');
  const systemEl = $('admin-system');
  const secretsEl = $('admin-secrets');
  const usageEl = $('admin-usage');
  const auditEl = $('admin-audit');
  const worldRulesEl = $('admin-world-rules');
  const mintEl = $('admin-mint');
  const llmModelEl = $('admin-llm-model');
  const jobForm = $('admin-job-form') as HTMLFormElement;
  const back = $('admin-back') as HTMLButtonElement;
  const error = $('admin-error');
  const info = $('admin-info');

  greeting.textContent = `${user.display_name} · admin`;
  screen.hidden = false;
  error.textContent = '';
  info.hidden = true;

  const ac = new AbortController();
  const on = (el: HTMLElement, ev: string, fn: (e: Event) => void): void =>
    el.addEventListener(ev, fn, { signal: ac.signal });

  const ctx: AdminPanelCtx = {
    user,
    setError: (msg) => { error.textContent = msg; },
    setInfo: (msg) => { info.textContent = msg; info.hidden = false; },
  };

  // Sidebar nav → toggle the matching <section data-section>.
  const navButtons = Array.from(screen.querySelectorAll<HTMLButtonElement>('.admin-nav button'));
  const sections = Array.from(screen.querySelectorAll<HTMLElement>('.admin-section'));
  const showPanel = (panel: string): void => {
    for (const b of navButtons) b.classList.toggle('active', b.dataset.panel === panel);
    for (const s of sections) s.hidden = s.dataset.section !== panel;
  };
  for (const b of navButtons) on(b, 'click', () => showPanel(b.dataset.panel!));
  showPanel('overview');

  await Promise.all([
    renderOverview(overviewEl),
    renderUsers(usersEl, ctx),
    renderParties(partiesEl, ctx),
    renderSystem(systemEl, ctx),
    renderSecrets(secretsEl, ctx),
    renderAdminUsage(usageEl),
    renderAudit(auditEl),
    renderWorldRules(worldRulesEl),
    renderMintWorld(mintEl),
    renderLlmModel(llmModelEl),
  ]);

  on(jobForm, 'submit', async (e) => {
    e.preventDefault();
    error.textContent = '';
    info.hidden = true;
    const fd = new FormData(jobForm);
    const partyId = Number(fd.get('party_id'));
    const kind = String(fd.get('kind')) as AdminJobKind;
    if (!Number.isInteger(partyId) || partyId <= 0) { ctx.setError('invalid party id'); return; }
    if (!ADMIN_JOB_KINDS.includes(kind)) { ctx.setError('invalid kind'); return; }
    try {
      const r = await enqueueAdminJob(partyId, kind);
      ctx.setInfo(`Enqueued ${kind} for party ${partyId} (job #${r.job_id}).`);
      await Promise.all([renderSystem(systemEl, ctx), renderOverview(overviewEl)]);
    } catch (err) {
      ctx.setError((err as Error).message);
    }
  });

  return new Promise<void>((resolve) => {
    on(back, 'click', () => {
      ac.abort();
      screen.hidden = true;
      resolve();
    });
  });
}

// Overview stat cards — at-a-glance counts + today's Anthropic spend.
async function renderOverview(host: HTMLElement): Promise<void> {
  host.innerHTML = 'Loading…';
  try {
    const s = await getAdminSystem();
    host.innerHTML = '';
    host.appendChild(card('Users', String(s.counts.users)));
    host.appendChild(card('Parties', String(s.counts.parties)));
    host.appendChild(card('Messages', String(s.counts.messages)));
    host.appendChild(card(
      'Jobs pending', String(s.jobs.pending),
      s.jobs.failed > 0 ? `${s.jobs.failed} failed` : `${s.jobs.running} running`,
      s.jobs.failed > 0 ? 'warn' : undefined,
    ));
    host.appendChild(spendCard(s));
  } catch (err) {
    host.innerHTML = '';
    const e = document.createElement('div');
    e.className = 'party-meta';
    e.textContent = `overview: ${(err as Error).message}`;
    host.appendChild(e);
  }
}

function card(label: string, value: string, sub?: string, tone?: 'warn' | 'good'): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'stat-card' + (tone ? ' ' + tone : '');
  el.innerHTML = `<div class="sc-label">${label}</div><div class="sc-value">${value}</div>${
    sub ? `<div class="sc-sub">${sub}</div>` : ''
  }`;
  return el;
}

function spendCard(s: AdminSystem): HTMLDivElement {
  const spend = s.anthropic.spend_today_usd;
  const cap = s.anthropic.cap_usd;
  const over = cap !== null && spend >= cap;
  const tone = over ? 'warn' : cap !== null && spend > cap * 0.8 ? 'good' : undefined;
  const sub = cap === null ? 'no cap' : `cap $${cap.toFixed(2)}`;
  return card('Spend today', `$${spend.toFixed(2)}`, sub, tone);
}
